import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner } from '@cowoo/shared/ui-web';
import { ApiProblem, formatMoney, newIdempotencyKey, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, Field, fieldClass } from '../../ui';

type Refund = 'card' | 'credit' | 'none';

/**
 * "Gutschrift erstellen" (10-web-admin § 13, BR-PAY-07).
 *
 * The three refund modes are three different things and the copy says which:
 * `card` sends money back to the card, `credit` puts it on the account for the
 * next charge, `none` issues the document alone and corrects the books without
 * moving anything.
 *
 * The amount is capped at what is left of the invoice, not at its total — two
 * partial credit notes must not add up to more than was ever charged. The
 * server enforces the same bound; this only spares staff a refused request.
 *
 * The staff refund limit is **not** mirrored here. It lives in
 * `billing.staff_refund_limit_cents`, the screen does not read settings, and a
 * guessed cap that disagrees with the server would be worse than none — so a
 * refund over the limit is refused by the server and shown as `forbidden`.
 */
export function CreditNoteDialog({
  invoiceId,
  remainingCents,
  onClose,
}: {
  invoiceId: string;
  remainingCents: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(String(remainingCents / 100).replace('.', ','));
  const [refund, setRefund] = useState<Refund>('credit');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const cents = Math.round(Number(amount.replace(',', '.')) * 100);
  const valid = Number.isInteger(cents) && cents > 0 && cents <= remainingCents && reason.trim().length >= 5;

  /**
   * One key per intent, replaced when the body changes (04 § 8.5).
   *
   * A fixed key would make a corrected amount collide with the first attempt
   * (`idempotency_conflict`); a fresh key per click would let a double submit
   * refund twice. Keying it to the body is exactly the rule the architecture
   * doc states, and here the body is small enough to write out.
   */
  const body = { invoiceId, grossCents: cents, refund, reason: reason.trim() };
  const signature = JSON.stringify(body);
  const key = useRef<{ signature: string; value: string } | null>(null);
  if (key.current?.signature !== signature) key.current = { signature, value: newIdempotencyKey() };

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/credit-notes', {
        params: { header: { 'idempotency-key': key.current!.value } },
        body,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('invoices') });
      onClose();
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  return (
    <Dialog
      title={t('admin.invoices.creditNote.title')}
      confirmLabel={t('admin.invoices.creditNote.confirm', {
        amount: formatMoney(Number.isFinite(cents) ? cents : 0),
      })}
      onConfirm={() => create.mutate()}
      onClose={onClose}
      busy={create.isPending}
      disabled={!valid}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <div className="flex gap-2.5">
        <div className="flex-1">
          <Field
            label={t('admin.invoices.creditNote.amount')}
            hint={t('admin.invoices.creditNote.remaining', { amount: formatMoney(remainingCents) })}
          >
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              className={fieldClass}
            />
          </Field>
        </div>
        <div className="flex-1">
          <Field label={t('admin.invoices.creditNote.refund')}>
            <select
              value={refund}
              onChange={(event) => setRefund(event.target.value as Refund)}
              className={fieldClass}
            >
              <option value="credit">{t('admin.invoices.creditNote.mode.credit')}</option>
              <option value="card">{t('admin.invoices.creditNote.mode.card')}</option>
              <option value="none">{t('admin.invoices.creditNote.mode.none')}</option>
            </select>
          </Field>
        </div>
      </div>

      <Field label={t('admin.invoices.creditNote.reason')} hint={t('admin.reasonHint')}>
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('admin.reasonPlaceholder')}
          className={fieldClass}
        />
      </Field>

      <p className="text-adminSmall text-muted leading-normal">
        {t(`admin.invoices.creditNote.hint.${refund}`)}
      </p>
    </Dialog>
  );
}
